import { isNil } from "@hopper-ui/components";
import { MixpanelEventName, MixpanelProperty } from "@sg-protect/shell";
import type { DOMEventTelemetryHandler } from "./domEventTracker.ts";
import { findCheckboxText, getModalInformation, getRole, getTelemetryName } from "./utils.ts";

export const checkboxChangeHandler: DOMEventTelemetryHandler<"change"> = {
    isHandling: evt => {
        if (isNil(evt.target) || (evt.target as HTMLElement).tagName !== "INPUT" || isHopperCheckbox(evt.target)) {
            return false;
        }

        return (evt.target as HTMLInputElement).type === "checkbox";
    },
    handleEvent: (evt, onEvent) => {
        const checkbox = evt.target as HTMLInputElement;
        const text = findCheckboxText(checkbox);
        const role = getRole(checkbox);
        const modalInformation = getModalInformation(checkbox);

        switch (role) {
            case "switch": {
                void onEvent(MixpanelEventName.SwitchChanged, {
                    [MixpanelProperty.Name]: text,
                    [MixpanelProperty.IsChecked]: checkbox.checked,
                    [MixpanelProperty.Modal]: modalInformation
                });
                break;
            }
            default: {
                void onEvent(MixpanelEventName.CheckboxChanged, {
                    [MixpanelProperty.Name]: text,
                    [MixpanelProperty.IsChecked]: checkbox.checked,
                    [MixpanelProperty.Modal]: modalInformation
                });
            }
        }
    }
};

// Tracking checkbox changes in a separate handler because it the change event is not triggered
// when the checkbox state changes by clicking on the label.
export const hopperCheckboxClickHandler: DOMEventTelemetryHandler<"click"> = {
    isHandling: evt => !isNil(evt.target) && isHopperCheckbox(evt.target),
    handleEvent: (evt, onEvent) => {
        const target = evt.target as HTMLElement;
        const label = target.closest("label");
        const input = label?.querySelector("input[type='checkbox']") as HTMLInputElement | null;
        const text = getTelemetryName(label as HTMLElement);
        const role = getRole(input as HTMLElement);
        const modalInformation = getModalInformation(target);

        let isChecked = input?.checked ?? false;
        if (target !== input) {
            isChecked = !isChecked; // When clicking on the label, Hopper will propagate the state change but the state is not applied yet.
        }

        switch (role) {
            case "switch": {
                void onEvent(MixpanelEventName.SwitchChanged, {
                    [MixpanelProperty.Name]: text,
                    [MixpanelProperty.IsChecked]: isChecked,
                    [MixpanelProperty.Modal]: modalInformation
                });
                break;
            }
            default: {
                void onEvent(MixpanelEventName.CheckboxChanged, {
                    [MixpanelProperty.Name]: text,
                    [MixpanelProperty.IsChecked]: isChecked,
                    [MixpanelProperty.Modal]: modalInformation
                });
            }
        }
    }
};

function isHopperCheckbox(eventTarget: EventTarget): boolean {
    return (eventTarget as HTMLElement).closest("label")?.className.includes("hop-Checkbox") ?? false;
}
